import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi } from '../services/api';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner, Modal, Input } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const AdminUsers = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Edit Modal State
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    balance: 0,
    isAdmin: false,
    isDriver: false,
    isOperator: false,
    status: 'ACTIVE'
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getUsers();
      setUsers(data || []);
    } catch (err) {
      toast.error(t('dashboard.errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleBlock = async (userId, isBlocked) => {
    try {
      await adminApi.blockUser(userId, !isBlocked);
      toast.success(isBlocked ? t('dashboard.userActivated') : t('dashboard.userBlocked'));
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || '',
      balance: user.balance || 0,
      isAdmin: user.isAdmin || false,
      isDriver: user.isDriver || false,
      isOperator: user.isOperator || false,
      status: user.status || 'ACTIVE'
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await adminApi.updateUser(selectedUser.id, editFormData);
      toast.success(t('dashboard.userInfoUpdated'));
      setIsEditModalOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || t('setPassword.genericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredUsers = users.filter(u => 
    (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.phone || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedUsers = filteredUsers.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="DATABASE"
        title={t('dashboard.userManagement') || 'User Management'}
        subtitle={`${users.length} ${t('dashboard.totalUsers') || 'TOTAL USERS IN SYSTEM'}`}
        icon="group"
      >
        <div className="relative group flex-1">
          <input
            type="text"
            placeholder={t('dashboard.searchPlaceholder') || 'Qidiruv...'}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-80 h-14 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-3xl px-12 outline-none focus:border-[#1e9f6b]/50 transition-all font-bold text-sm dark:text-white shadow-xl shadow-slate-900/5"
          />
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#1e9f6b] transition-colors">
            search
          </span>
        </div>
      </AdminPageHeader>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/5">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-900/30">
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.user')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.role')}</th>
                  <th className="hidden lg:table-cell px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.lastSeen')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.balance')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.status')}</th>
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em] text-right">{t('dashboard.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                <AnimatePresence mode="popLayout">
                  {paginatedUsers.map((u, idx) => (
                    <motion.tr 
                      key={u.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ delay: idx * 0.05 }}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-300 group"
                    >
                      <td className="px-10 py-6">
                        <div className="flex items-center gap-5">
                          <div className={`h-12 w-12 rounded-2xl flex items-center justify-center overflow-hidden transition-all duration-500 shadow-inner italic ${
                            u.isAdmin ? "bg-indigo-500 text-white" : 
                            u.isDriver ? "bg-amber-500 text-white" : 
                            "bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:bg-[#1e9f6b] group-hover:text-white"
                          }`}>
                            {u.avatarUrl ? (
                              <img src={u.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span className="font-black text-sm">{u.name?.[0] || 'U'}</span>
                            )}
                          </div>
                          <div>
                            <div className="font-black text-sm italic uppercase tracking-tight text-slate-900 dark:text-white">{u.name || t('operatorDashboard.customer')}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{u.phone || u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <Badge variant="slate" className="rounded-xl font-black italic uppercase text-[8px] tracking-wider px-3 py-1.5 border-0 bg-slate-100 dark:bg-slate-800 text-slate-500">
                           {u.role}
                        </Badge>
                      </td>
                      <td className="hidden lg:table-cell px-6 py-6">
                        <div className="space-y-1">
                          <div className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-tight italic">
                            {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                          </div>
                          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest truncate max-w-[120px]">
                            {u.lastDeviceInfo || u.lastLoginIp || 'Ma\'lumot yo\'q'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <div className="text-xs font-black italic uppercase tracking-tighter text-[#1e9f6b]">
                          {(u.balance || 0).toLocaleString()} <span className="text-[8px] opacity-60 ml-0.5">{t('common.uzs')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-6">
                        <Badge 
                          variant={u.isBlocked ? 'red' : 'green'} 
                          pulse={!u.isBlocked}
                          className="rounded-xl font-black italic uppercase text-[8px] tracking-wider px-3 py-1.5 border-0"
                        >
                          {u.isBlocked ? t('dashboard.blocked') || 'BLOCKED' : t('dashboard.active') || 'ACTIVE'}
                        </Badge>
                      </td>
                      <td className="px-10 py-6 text-right">
                        <div className="flex justify-end gap-3 opacity-100 transition-opacity duration-300">
                          <Button size="xs" variant="ghost" className="h-10 w-10 rounded-xl p-0 hover:bg-indigo-500/10 hover:text-indigo-500" onClick={() => handleEditClick(u)}>
                            <span className="material-symbols-outlined text-[20px]">edit</span>
                          </Button>
                          <Button 
                            size="xs" 
                            variant="ghost" 
                            className={`h-10 w-10 rounded-xl p-0 transition-all ${u.isBlocked ? 'text-emerald-500 bg-emerald-500/10' : 'text-rose-500 bg-rose-500/10'}`}
                            onClick={() => handleToggleBlock(u.id, u.isBlocked)}
                          >
                            <span className="material-symbols-outlined text-[20px]">{u.isBlocked ? 'lock_open' : 'lock'}</span>
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-10 py-8 border-t border-slate-50 dark:border-slate-800/50 flex justify-between items-center bg-slate-50/10 dark:bg-slate-800/5">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">
                {t('common.pageOf', { page, total: totalPages })}
              </div>
              <div className="flex gap-3">
                <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  <span className="material-symbols-outlined text-sm mr-2">arrow_back</span>
                  {t('common.prev')}
                </Button>
                <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  {t('common.next')}
                  <span className="material-symbols-outlined text-sm ml-2">arrow_forward</span>
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Edit User Modal */}
      <Modal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)}
        title="Foydalanuvchini tahrirlash"
      >
        <form onSubmit={handleUpdateUser} className="space-y-6">
           <Input 
              label="Ism sharif"
              value={editFormData.name}
              onChange={e => setEditFormData({...editFormData, name: e.target.value})}
              required
           />
           <Input 
              label="Balans (UZS)"
              type="number"
              value={editFormData.balance}
              onChange={e => setEditFormData({...editFormData, balance: parseInt(e.target.value)})}
              required
           />
           
           <div className="grid grid-cols-3 gap-4">
              <div className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${editFormData.isAdmin ? 'border-indigo-500 bg-indigo-50/50' : 'border-slate-100 opacity-60'}`} onClick={() => setEditFormData({...editFormData, isAdmin: !editFormData.isAdmin})}>
                 <div className="font-black text-[10px] uppercase italic text-center">Admin</div>
              </div>
              <div className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${editFormData.isDriver ? 'border-amber-500 bg-amber-50/50' : 'border-slate-100 opacity-60'}`} onClick={() => setEditFormData({...editFormData, isDriver: !editFormData.isDriver})}>
                 <div className="font-black text-[10px] uppercase italic text-center">Driver</div>
              </div>
              <div className={`p-4 rounded-2xl border-2 transition-all cursor-pointer ${editFormData.isOperator ? 'border-blue-500 bg-blue-50/50' : 'border-slate-100 opacity-60'}`} onClick={() => setEditFormData({...editFormData, isOperator: !editFormData.isOperator})}>
                 <div className="font-black text-[10px] uppercase italic text-center">Operator</div>
              </div>
           </div>

           <div className="pt-6 flex gap-4">
             <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)} className="flex-1">Bekor qilish</Button>
             <Button type="submit" variant="emerald" loading={isSubmitting} className="flex-1">Saqlash</Button>
           </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminUsers;